import { designVariables as tk } from "@studio3t/design-variables";
import {
  GenericDropdown,
  GenericDropdownContext,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import type { PipelineView } from "@/pages/NewStudio/pipelineView";
import { ChipBar } from "./AttachmentChips";
import { ChatDeleteModal } from "./ChatDeleteModal";
import { ChatInput } from "./ChatInput";
import { ChatNameModal } from "./ChatNameModal";
import { ChatWindow } from "./ChatWindow";
import { useAiChatSidebarState } from "./useAiChatSidebarState";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface StudioScript {
  name: string;
  content: string;
  serviceName?: string;
}

export interface StudioTestCase {
  name: string;
  input: unknown;
  expected?: unknown;
  assertions: unknown[];
  passed?: boolean;
  actual?: unknown;
  error?: string;
}

export interface AiChatSidebarProps {
  scripts?: StudioScript[];
  inputDocument?: string;
  outputDocument?: string;
  schema?: string;
  lastError?: string;
  testCases?: StudioTestCase[];
  workspaceId?: string | null;
  workspaceName?: string | null;
  linkedScripts?: number;
  hasLinkedSchema?: boolean;
  hasLinkedTestSuite?: boolean;
  inputOrigin?: { serviceName: string; resource: string } | null;
  schemaId?: string;
  schemaServiceName?: string;
  testSuiteId?: string | null;
  pipelineView?: PipelineView;
  unresolvedServices?: Set<string>;
  onApplyCode?: (
    code: string,
    lang: string,
    artifact: string | null,
    name: string | null,
  ) => void;
  isOpen?: boolean;
  onClose?: () => void;
  /** Attach every non-empty script by default, not only on an error —
   *  the new Studio's rule (all staged transforms, deselectable). */
  attachScriptsByDefault?: boolean;
  /** The product-surface layer of the system prompt for this host; the
   *  old Studio's is the default. */
  systemPrompt?: string;
  /** "attach" (default): the chips pick what to send, reset per message.
   *  "follows-view": everything on screen is sent; the chips hide items,
   *  sticky for the conversation (the new Studio). */
  contextMode?: "attach" | "follows-view";
}

// ---------------------------------------------------------------------------
// Attachment types and helpers
// ---------------------------------------------------------------------------

/** Attachment keys — scripts are keyed by name */
export type AttachmentKey =
  | "input"
  | "schema"
  | "tests"
  | "pipeline"
  | `script:${string}`;

/** Compute smart defaults based on current state */
export function getDefaultAttachments(
  props: AiChatSidebarProps,
): Set<AttachmentKey> {
  const defaults = new Set<AttachmentKey>();

  // Auto-attach script(s) if there's an error (or always, when the host
  // asks for it)
  if (props.lastError?.trim() || props.attachScriptsByDefault) {
    const scripts = props.scripts?.filter((s) => s.content.trim()) ?? [];
    for (const s of scripts) {
      defaults.add(`script:${s.name}`);
    }
  }

  // Auto-attach tests when any case has a failure result
  if (props.testCases?.some((tc) => tc.passed === false)) {
    defaults.add("tests");
  }

  return defaults;
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

interface ChatHeaderProps {
  onNewChat: () => void;
  newChatDisabled?: boolean;
  onRenameChat?: () => void;
  onDeleteChat?: () => void;
  conversations: { id: string; title: string }[];
  currentId?: string;
  onSelectConversation: (id: string) => void;
  onClose?: () => void;
}

// The chat menu (Figma DropdownMenu 14669-187923): the chats as the
// scrolling rows, the list-wide actions pinned under them as the
// dropdown's footer (TTTL-316) so a long chat history never scrolls them
// out of reach.
function buildChatRows(
  conversations: { id: string; title: string }[],
  currentId: string | undefined,
  onSelectConversation: (id: string) => void,
): ListItemProps[][] {
  if (conversations.length === 0) return [];
  return [
    conversations.map((conv) => ({
      id: conv.id,
      label: conv.title,
      type: "text" as const,
      value: conv.id,
      selected:
        conv.id === currentId
          ? { value: true, icon: "dot" as const }
          : undefined,
      onClick: () => onSelectConversation(conv.id),
    })),
  ];
}

function buildChatActions(
  onNewChat: () => void,
  newChatDisabled?: boolean,
  onRenameChat?: () => void,
  onDeleteChat?: () => void,
): ListItemProps[] {
  const actions: ListItemProps[] = [
    {
      id: "new-chat",
      label: "New chat",
      type: "text" as const,
      value: "new-chat",
      leadingIcon: Icon.Plus,
      disabled: newChatDisabled,
      onClick: newChatDisabled ? undefined : onNewChat,
    },
  ];

  if (onRenameChat) {
    actions.push({
      id: "rename-chat",
      label: "Rename selected chat",
      type: "text" as const,
      value: "rename-chat",
      leadingIcon: Icon.Edit,
      onClick: onRenameChat,
    });
  }

  if (onDeleteChat) {
    actions.push({
      id: "delete-chat",
      label: "Delete selected chat",
      type: "text" as const,
      value: "delete-chat",
      leadingIcon: Icon.Bin,
      leadingIconColorKey: SVGColorKey.errorIcon,
      onClick: onDeleteChat,
    });
  }

  return actions;
}

function ChatHeader({
  onNewChat,
  newChatDisabled,
  onRenameChat,
  onDeleteChat,
  conversations,
  currentId,
  onSelectConversation,
  onClose,
}: ChatHeaderProps) {
  const chatRows = useMemo(
    () => buildChatRows(conversations, currentId, onSelectConversation),
    [conversations, currentId, onSelectConversation],
  );
  const chatActions = useMemo(
    () =>
      buildChatActions(onNewChat, newChatDisabled, onRenameChat, onDeleteChat),
    [onNewChat, newChatDisabled, onRenameChat, onDeleteChat],
  );

  return (
    <Header>
      <HeaderLeft>
        <HeaderTitle>AI Helper</HeaderTitle>
      </HeaderLeft>
      <HeaderActions>
        <GenericDropdown
          dropdownItems={chatRows}
          footer={chatActions}
          width={{ strategy: "fixed", size: 260 }}
        >
          <GenericDropdownContext.Consumer>
            {({ open }) => (
              <ChatOptionsButton
                title="Chat options"
                data-testid="chat-options-btn"
                $active={open}
              >
                <IconSVG
                  icon={Icon.Chat}
                  size={16}
                  svgColorKey={SVGColorKey.semanticTextBody}
                />
                <IconSVG
                  icon={Icon.BaseChevron}
                  size={10}
                  svgColorKey={SVGColorKey.semanticTextBody}
                />
              </ChatOptionsButton>
            )}
          </GenericDropdownContext.Consumer>
        </GenericDropdown>
        {onClose && (
          <CollapseButton
            onClick={onClose}
            title="Collapse AI Helper"
            data-testid="close-sidebar-btn"
          >
            <IconSVG
              icon={Icon.CollapseRight}
              size={16}
              svgColorKey={SVGColorKey.semanticTextBody}
            />
          </CollapseButton>
        )}
      </HeaderActions>
    </Header>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

/** Rename/delete modal state for the current conversation. */
function useConversationModals(
  currentConversationId: string | null,
  conversations: { id: string; title: string }[],
  renameConversation: (id: string, title: string) => void,
  deleteConversation: (id: string) => Promise<void>,
) {
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const currentTitle =
    conversations.find((c) => c.id === currentConversationId)?.title ?? "";

  const saveRename = useCallback(
    (name: string) => {
      if (currentConversationId) {
        renameConversation(currentConversationId, name);
      }
    },
    [currentConversationId, renameConversation],
  );

  const confirmDelete = useCallback(() => {
    if (currentConversationId) void deleteConversation(currentConversationId);
  }, [currentConversationId, deleteConversation]);

  return {
    renameOpen,
    deleteOpen,
    currentTitle,
    openRename: useCallback(() => setRenameOpen(true), []),
    closeRename: useCallback(() => setRenameOpen(false), []),
    openDelete: useCallback(() => setDeleteOpen(true), []),
    closeDelete: useCallback(() => setDeleteOpen(false), []),
    saveRename,
    confirmDelete,
  };
}

type ConversationModalsState = ReturnType<typeof useConversationModals>;

function ConversationModals({ modals }: { modals: ConversationModalsState }) {
  return (
    <>
      <ChatNameModal
        isOpen={modals.renameOpen}
        mode="rename"
        defaultName={modals.currentTitle}
        onClose={modals.closeRename}
        onSave={modals.saveRename}
      />
      <ChatDeleteModal
        isOpen={modals.deleteOpen}
        chatTitle={modals.currentTitle || undefined}
        onClose={modals.closeDelete}
        onConfirm={modals.confirmDelete}
      />
    </>
  );
}

/** New chat is pointless while the active chat (or the whole list) is empty. */
function isNewChatDisabled(
  conversations: unknown[],
  currentConversationId: string | null,
  messages: unknown[],
): boolean {
  if (conversations.length === 0) return true;
  return currentConversationId != null && messages.length === 0;
}

export const AiChatSidebar: React.FC<AiChatSidebarProps> = (props) => {
  const { onApplyCode, isOpen = true, onClose } = props;

  const {
    attachments,
    truncated,
    testFailureCount,
    toggleAttachment,
    nonEmptyScripts,
    hasInput,
    hasSchema,
    hasError,
    hasTests,
    hasPipeline,
    pipelineUnparsed,
    handleSubmit,
    messages,
    messagesLoaded,
    currentConversationId,
    conversations,
    inputValue,
    setInputValue,
    isLoading,
    newConversation,
    selectConversation,
    deleteConversation,
    renameConversation,
    stopGeneration,
  } = useAiChatSidebarState(props);

  const modals = useConversationModals(
    currentConversationId,
    conversations,
    renameConversation,
    deleteConversation,
  );

  if (!isOpen) return null;

  const hasCurrentChat = currentConversationId != null;

  return (
    <Container data-testid="ai-chat-sidebar">
      <ChatHeader
        onNewChat={() => newConversation()}
        newChatDisabled={isNewChatDisabled(
          conversations,
          currentConversationId,
          messages,
        )}
        onRenameChat={hasCurrentChat ? modals.openRename : undefined}
        onDeleteChat={hasCurrentChat ? modals.openDelete : undefined}
        conversations={conversations}
        currentId={currentConversationId ?? undefined}
        onSelectConversation={selectConversation}
        onClose={onClose}
      />
      <ConversationModals modals={modals} />
      <ChatWindow
        messages={messages}
        isThinking={isLoading}
        loading={currentConversationId != null && !messagesLoaded}
        onApplyCode={onApplyCode}
      />
      <ChipBar
        mode={props.contextMode ?? "attach"}
        scripts={nonEmptyScripts}
        hasInput={hasInput}
        hasSchema={hasSchema}
        hasTests={hasTests}
        testFailures={testFailureCount}
        hasPipeline={hasPipeline}
        pipelineUnparsed={pipelineUnparsed}
        hasError={hasError}
        attachments={attachments}
        onToggle={toggleAttachment}
        truncated={truncated}
      />
      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSubmit={handleSubmit}
        onAbort={stopGeneration}
        isLoading={isLoading}
        placeholder="Ask about your transformation script"
      />
    </Container>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${(p) => p.theme.cards1Background};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${tk.ui.semantic.spacingm}px ${tk.ui.semantic.spacingm}px 8px;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
`;

const HeaderTitle = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${(p) => p.theme.semanticTextLight};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChatOptionsButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 28px;
  padding: 0 8px;
  box-sizing: border-box;
  background: ${(p) => (p.$active ? p.theme.cards2Background : "transparent")};
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.cards2Background};
  }
`;

const CollapseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 8px;
  box-sizing: border-box;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.cards2Background};
  }
`;

export default AiChatSidebar;
